import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, CircularProgress, Card, CardContent, Skeleton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  ReceiptLong as InvoiceSettingsIcon,
  WorkspacePremium as SubscriptionIcon,
  AccountBalanceWallet as WalletIcon,
  ChevronRight as ChevronRightIcon,
  Handshake as OwnersIcon,
  Palette as BrandingIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import CompanyConfig from '../CompanyConfig';
import TenantSettings from '../Admin/TenantSettings';
import profitShareService from '../../services/accounting/profitShare.service';
import { can } from '../../utils/roleUtils';
import { resolveImageUrl } from '../../utils/colorUtils';

const sectionLabelSx = {
  fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em',
  color: 'text.secondary', textTransform: 'uppercase', mb: 1
};

const BUSINESS_TYPES = { SOLE_ESTABLISHMENT: 'Sole establishment', COMPANY: 'Company (LLC)' };

const parseTheme = (raw) => {
  if (!raw) return {};
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
};

// Company Config is serialized snake_case; camelCase kept as a fallback.
const pick = (data, snake, camel) => (data?.[snake] ?? data?.[camel] ?? '');

// isConnected only reflects a cached token, which expires and is renewed on demand,
// so a saved login is the meaningful signal here.
const CONNECTION_STATES = {
  connected: { label: 'Connected', color: 'success', variant: 'filled' },
  notConfigured: { label: 'Login not set', color: 'warning', variant: 'outlined' },
  servicesOff: {
    label: 'Thaklis services off', color: 'default', variant: 'outlined',
    tooltip: 'Turn on Thaklis services in Work Order Settings to create Thaklis tickets.'
  },
};

const connectionStateOf = (status, servicesOff) => {
  if (servicesOff) return CONNECTION_STATES.servicesOff;
  if (!status) return null;
  return status.hasCredentials ? CONNECTION_STATES.connected : CONNECTION_STATES.notConfigured;
};

const initialsOf = (name) => (name || '')
  .replace(/\[[^\]]*\]/g, '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0].toUpperCase())
  .join('') || '?';

const Field = ({ label, value, grow = 1 }) => (
  <Box sx={{ flex: `${grow} 1 200px`, px: 2, py: 1.5, minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: '0.8125rem', whiteSpace: 'pre-line', wordBreak: 'break-word',
        fontWeight: value ? 500 : 400, color: value ? 'text.primary' : 'text.disabled'
      }}
    >
      {value || 'Not set'}
    </Typography>
  </Box>
);

const OurCompanyTab = ({
  currentUser,
  typingCenterId,
  isPlatform,
  thaklisServicesOff,
  accounts,
  accountsLoading,
  walletDataByOrg,
  ticketDataByOrg,
  canReadFinance,
  fmtMoney,
  onOpenSettings,
  statusRefreshKey,
  view = null,
  onViewChange = () => {},
}) => {
  const navigate = useNavigate();
  const canReadSettings = can(currentUser, 'settings', 'read');
  const canEditSettings = can(currentUser, 'settings', 'write');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [credentialStatuses, setCredentialStatuses] = useState({});
  const [logoFailed, setLogoFailed] = useState(false);
  const [owners, setOwners] = useState(null);
  const [typeDialog, setTypeDialog] = useState(null);
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeError, setTypeError] = useState(null);

  useEffect(() => {
    // Clear first so a previous typing center's details never show under the new one.
    setProfile(null);
    if (!typingCenterId) {
      setProfileLoading(false);
      return undefined;
    }
    let cancelled = false;
    setProfileLoading(true);

    const loadProfile = async () => {
      const [tcRes, configRes] = await Promise.allSettled([
        ApiService.getTypingCenterById(typingCenterId),
        canReadSettings ? ApiService.getCompanyConfigById(typingCenterId) : Promise.resolve(null),
      ]);
      if (cancelled) return;

      const tc = tcRes.status === 'fulfilled' ? (tcRes.value?.data?.data || tcRes.value?.data || {}) : {};
      const cfg = configRes.status === 'fulfilled' ? (configRes.value?.data?.data || {}) : {};
      const theme = parseTheme(tc.theme_config ?? tc.themeConfig);
      const streetParts = [
        pick(cfg, 'address_line1', 'addressLine1'),
        pick(cfg, 'address_line2', 'addressLine2'),
        [cfg.city, cfg.emirate].filter(Boolean).join(', '),
      ].filter(Boolean);
      const addressParts = streetParts.length ? [...streetParts, cfg.country].filter(Boolean) : [];

      setProfile({
        name: pick(cfg, 'company_name', 'companyName') || tc.name || '',
        nameArabic: pick(cfg, 'company_name_arabic', 'companyNameArabic'),
        code: tc.code || '',
        logoUrl: theme.logo_url || theme.logoUrl || '',
        trn: pick(cfg, 'trn_number', 'trnNumber'),
        tradeLicense: pick(cfg, 'trade_license_number', 'tradeLicenseNumber'),
        phone: cfg.telephone || cfg.mobile || tc.contact_number || tc.contactNumber || '',
        email: cfg.email || tc.email || '',
        address: addressParts.join(', '),
      });
      setLogoFailed(false);
      setProfileLoading(false);
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [typingCenterId, canReadSettings, view]); // reloaded when coming back from the details/branding editor

  // Owners come from Owner Drawings → Partners; a link means the partner is also a person on Team.
  useEffect(() => {
    if (!typingCenterId || !canReadFinance) {
      setOwners(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all([
      profitShareService.getPartners().catch(() => null),
      ApiService.get('/api/settings/accounting').catch(() => null),
    ]).then(([partnerResp, settingsResp]) => {
      if (cancelled) return;
      setOwners({
        businessType: settingsResp?.data?.data?.business_type === 'COMPANY' ? 'COMPANY' : 'SOLE_ESTABLISHMENT',
        partners: (partnerResp?.data || [])
          .filter((p) => p.active !== false)
          .map((p) => ({
            id: p.id,
            name: p.name,
            percent: Number(p.ownershipPercent),
            silent: p.role === 'SILENT',
            phone: p.phone || '',
            email: p.email || '',
            since: p.effectiveDate || '',
            onTeam: Boolean(p.userId),
          })),
      });
    });
    return () => { cancelled = true; };
  }, [typingCenterId, canReadFinance]);

  // Reloaded whenever the Settings dialog closes, so a credentials change shows at once.
  useEffect(() => {
    if (thaklisServicesOff || !accounts?.length) {
      setCredentialStatuses({});
      return undefined;
    }
    let cancelled = false;
    Promise.allSettled(
      accounts.map((acc) => ApiService.getOrganizationCredentialStatus(acc.mappingId || acc.id))
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          const acc = accounts[i];
          next[acc.mappingId || acc.id] = r.value?.data?.data || null;
        }
      });
      setCredentialStatuses(next);
    });
    return () => { cancelled = true; };
  }, [accounts, thaklisServicesOff, statusRefreshKey]);

  // Company details and branding open in place (they used to be Configuration › Company Settings / Branding).
  const editCompany = canReadSettings ? () => onViewChange('details') : null;
  const editBranding = canReadSettings ? () => onViewChange('branding') : null;

  const missingDetails = profile && canReadSettings ? [
    !profile.trn && 'TRN',
    !profile.tradeLicense && 'trade licence',
    !(profile.phone || profile.email) && 'contact',
    !profile.address && 'address',
  ].filter(Boolean) : [];

  const quickLinks = [
    currentUser?.role === 'TC_ADMIN' && { label: 'Team', icon: <PeopleIcon />, path: '/team' },
    canReadSettings && { label: 'Invoice settings', icon: <InvoiceSettingsIcon />, path: '/settings?section=documents&tab=invoices' },
    can(currentUser, 'subscription', 'read') && { label: 'Subscription', icon: <SubscriptionIcon />, path: '/settings/subscription' },
    canReadFinance && { label: 'Wallets', icon: <WalletIcon />, path: '/wallets' },
  ].filter(Boolean);

  if (!typingCenterId) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Select a typing center above to see its company details.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (view === 'details' || view === 'branding') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button size="small" startIcon={<BackIcon fontSize="small" />} onClick={() => onViewChange(null)}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
            Our Company
          </Button>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>/</Typography>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {view === 'details' ? 'Company details' : 'Branding'}
          </Typography>
        </Box>
        {view === 'details'
          ? <CompanyConfig currentUser={currentUser} embedded />
          : <TenantSettings currentUser={currentUser} embedded />}
      </Box>
    );
  }

  return (
    <Box>
      {/* Company profile */}
      <Paper variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          {profileLoading || !profile ? (
            <Skeleton variant="rounded" width={56} height={56} />
          ) : profile.logoUrl && !logoFailed ? (
            <Box
              component="img"
              src={resolveImageUrl(profile.logoUrl)}
              alt={`${profile.name} logo`}
              onError={() => setLogoFailed(true)}
              sx={{
                width: 56, height: 56, objectFit: 'contain', borderRadius: 1.5, flexShrink: 0,
                border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 0.5
              }}
            />
          ) : (
            <Box
              sx={{
                width: 56, height: 56, borderRadius: 1.5, bgcolor: 'primary.main', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <Typography sx={{ color: 'white', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '0.02em' }}>
                {initialsOf(profile.name)}
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {profileLoading || !profile ? (
              <>
                <Skeleton width="40%" height={28} />
                <Skeleton width="25%" />
              </>
            ) : (
              <>
                <Typography noWrap sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'secondary.main' }}>
                  {profile.name || 'Your company'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {[profile.nameArabic, profile.code, 'Typing Center'].filter(Boolean).join(' · ')}
                </Typography>
              </>
            )}
          </Box>
          {editBranding && (
            <Button
              size="small"
              startIcon={<BrandingIcon fontSize="small" />}
              onClick={editBranding}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', flexShrink: 0 }}
            >
              Branding
            </Button>
          )}
          {editCompany && (
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={editCompany}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', flexShrink: 0 }}
            >
              {canEditSettings ? 'Edit details' : 'View details'}
            </Button>
          )}
        </Box>

        {canReadSettings && profile && !profileLoading && (
          <>
            {missingDetails.length > 0 && (
              <Alert
                severity="info"
                icon={false}
                sx={{
                  borderRadius: 0, borderTop: '1px solid', borderColor: 'divider', py: 0.5,
                  alignItems: 'center', '& .MuiAlert-message': { fontSize: '0.8125rem' }
                }}
                action={editCompany && (
                  <Button size="small" onClick={editCompany} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Complete profile
                  </Button>
                )}
              >
                <strong>Finish your company profile.</strong>{' '}
                Add your {missingDetails.join(', ').replace(/, ([^,]*)$/, ' and $1')} so they appear on your invoices and receipts.
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex', flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider',
                '& > *:not(:last-child)': { borderRight: { sm: '1px solid' }, borderColor: { sm: 'divider' } }
              }}
            >
              <Field label="TRN" value={profile.trn} />
              <Field label="Trade licence" value={profile.tradeLicense} />
              <Field label="Contact" value={[profile.phone, profile.email].filter(Boolean).join('\n')} />
              <Field label="Address" value={profile.address} grow={2} />
            </Box>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: owners ? '1fr 1fr' : '1fr' }, gap: 2, mt: 3, alignItems: 'start' }}>
      <Box sx={{ minWidth: 0 }}>
      {/* Thaklis connection */}
      <Typography sx={sectionLabelSx}>Thaklis connection</Typography>
      {accountsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !accounts?.length ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LinkIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
              No Thaklis account linked
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {isPlatform
                ? 'Click "Add Organization" to link this typing center to its Thaklis account.'
                : 'Your typing center is not linked to a Thaklis account yet. Contact Thaklis support to set it up.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {accounts.map((acc) => {
            const orgId = acc.organizationId || acc.id;
            const state = connectionStateOf(credentialStatuses[acc.mappingId || acc.id], thaklisServicesOff);
            const walletData = canReadFinance ? walletDataByOrg[orgId] : null;
            const walletBalance = walletData ? Number(walletData.balance ?? 0) : null;
            const ticketData = ticketDataByOrg[orgId] || {};
            const paid = ticketData.paidTicketPaymentsAmount > 0;
            const pending = ticketData.pendingTicketPaymentsAmount > 0;
            const figures = [
              walletBalance != null ? { label: 'Wallet balance', value: fmtMoney(walletBalance) } : null,
              paid ? {
                label: `Ticket payments paid (${ticketData.paidTicketCount || 0})`,
                value: `AED ${(ticketData.paidTicketPaymentsAmount || 0).toLocaleString()}`
              } : null,
              pending ? {
                label: `Ticket payments pending (${ticketData.pendingTicketCount || 0})`,
                value: `AED ${(ticketData.pendingTicketPaymentsAmount || 0).toLocaleString()}`,
                color: '#ff5722'
              } : null,
            ].filter(Boolean);
            const needsSetup = state === CONNECTION_STATES.notConfigured;

            return (
              <Paper key={orgId} variant="outlined">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    <LinkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {acc.organizationName || acc.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {[acc.organizationCode || acc.code, 'Thaklis account'].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {state && (
                    <Tooltip title={state.tooltip || ''}>
                      <Chip
                        label={state.label}
                        color={state.color}
                        size="small"
                        variant={state.variant}
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    color={needsSetup ? 'warning' : 'primary'}
                    startIcon={<SettingsIcon fontSize="small" />}
                    onClick={(e) => onOpenSettings(acc, e)}
                    sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
                  >
                    {needsSetup ? 'Set up' : 'Settings'}
                  </Button>
                </Box>
                {figures.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex', flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider',
                      '& > *:not(:last-child)': { borderRight: '1px solid', borderColor: 'divider' }
                    }}
                  >
                    {figures.map((f) => (
                      <Box key={f.label} sx={{ flex: '1 1 160px', px: 2, py: 1.25 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.25 }}>{f.label}</Typography>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: f.color || 'text.primary' }}>
                          {f.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
      </Box>

      {/* Owners */}
      {owners && (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={sectionLabelSx}>Owners</Typography>
          <Paper variant="outlined">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <OwnersIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{BUSINESS_TYPES[owners.businessType]}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {owners.businessType === 'COMPANY'
                    ? 'Owners take profit share; an owner who works here can also be paid a salary.'
                    : "Owners' pay is recorded as drawings, not salary."}
                </Typography>
              </Box>
              {currentUser?.role === 'TC_ADMIN' && (
                <Button
                  size="small"
                  onClick={() => { setTypeError(null); setTypeDialog(owners.businessType); }}
                  sx={{ textTransform: 'none', fontSize: '0.8125rem', flexShrink: 0 }}
                >
                  Change type
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/accounting/partners')}
                sx={{ textTransform: 'none', fontSize: '0.8125rem', flexShrink: 0 }}
              >
                {owners.partners.length ? 'Manage' : 'Set up owners'}
              </Button>
            </Box>
            {owners.partners.length > 0 ? owners.partners.map((p) => (
              <Box
                key={p.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', bgcolor: 'secondary.main', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>{initialsOf(p.name)}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {p.name}
                    <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400, ml: 1 }}>
                      {p.silent ? 'Silent partner' : 'Working partner'}
                    </Typography>
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {[p.phone, p.email, p.since && `Since ${new Date(`${p.since}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`]
                      .filter(Boolean).join(' · ') || 'No contact details'}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={p.onTeam ? 'On Team' : 'Not on Team'}
                  color={p.onTeam ? 'success' : 'default'}
                  sx={{ fontSize: '0.6875rem', height: 22, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, width: 52, textAlign: 'right', flexShrink: 0 }}>{p.percent}%</Typography>
              </Box>
            )) : (
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                No owners set up yet. Add the owners and their share before recording drawings.
              </Typography>
            )}
          </Paper>
          <Dialog open={Boolean(typeDialog)} onClose={() => !typeSaving && setTypeDialog(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: '0.9375rem' }}>Business type</DialogTitle>
            <DialogContent>
              {typeError && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }}>{typeError}</Alert>}
              <RadioGroup value={typeDialog || ''} onChange={(e) => setTypeDialog(e.target.value)}>
                {Object.entries(BUSINESS_TYPES).map(([value, label]) => (
                  <FormControlLabel key={value} value={value} control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: '0.8125rem' }}>{label}</Typography>} />
                ))}
              </RadioGroup>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
                This decides from now on whether an owner can be paid a salary. Nothing already recorded changes.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTypeDialog(null)} disabled={typeSaving} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button variant="contained" disabled={typeSaving || typeDialog === owners.businessType}
                onClick={async () => {
                  setTypeSaving(true);
                  setTypeError(null);
                  try {
                    await ApiService.put('/api/settings/accounting', { business_type: typeDialog });
                    setOwners((o) => ({ ...o, businessType: typeDialog }));
                    setTypeDialog(null);
                  } catch (e) {
                    setTypeError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Could not change the business type.');
                  } finally {
                    setTypeSaving(false);
                  }
                }}
                sx={{ textTransform: 'none' }}>
                {typeSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      </Box>

      {/* Quick links */}
      {quickLinks.length > 0 && (
        <>
          <Typography sx={{ ...sectionLabelSx, mt: 3 }}>Quick links</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${quickLinks.length}, 1fr)` }, gap: 1.5 }}>
            {quickLinks.map((link) => (
              <Paper
                key={link.path}
                variant="outlined"
                component="button"
                type="button"
                onClick={() => navigate(link.path)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.25, cursor: 'pointer',
                  textAlign: 'left', font: 'inherit', color: 'text.primary', bgcolor: 'background.paper',
                  '& .MuiSvgIcon-root:first-of-type': { color: 'primary.main' },
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                {link.icon}
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, flexGrow: 1 }}>{link.label}</Typography>
                <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </Paper>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default OurCompanyTab;
